import React, { useState } from 'react';
import DateTimePickerInput from '../../../components/DateTimePickerInput';
import RichTextEditor from '../../../components/RichTextEditor';
import LookupField from '../../../components/lookup/LookupField';
import UserLookupModal from '../../../components/lookup/UserLookupModal';
import GroupLookupModal from '../../../components/lookup/GroupLookupModal';
import { CAB_BOARD_GROUP_TYPES } from '../../../constants/cabDefinitionOptions';
import { CAB_MEETING_STATE_OPTIONS } from '../../../constants/cabMeetingOptions';
import type { CabMeetingFormData } from '../../../types/cabMeeting';
import type { CabMeetingFormErrors } from '../../../utils/cabMeetingValidation';
import { formatGroupDisplayName, formatUserDisplayName } from '../../../components/lookup/lookupUtils';
import type { LookupGroup, LookupUser } from '../../../components/lookup/lookupTypes';

type LookupTarget = 'cabManager' | 'delegates' | 'boardMembers' | 'boardGroups' | null;
type ContentTab = 'conference' | 'notes';

interface CabMeetingFormProps {
  formData: CabMeetingFormData;
  errors: CabMeetingFormErrors;
  onChange: (updates: Partial<CabMeetingFormData>) => void;
  contentTab: ContentTab;
  onContentTabChange: (tab: ContentTab) => void;
  onNewMeetingInvite?: () => void;
  newMeetingInvitePending?: boolean;
}

const CabMeetingForm: React.FC<CabMeetingFormProps> = ({
  formData,
  errors,
  onChange,
  contentTab,
  onContentTabChange,
  onNewMeetingInvite,
  newMeetingInvitePending,
}) => {
  const [lookupTarget, setLookupTarget] = useState<LookupTarget>(null);

  const openLookup = (target: LookupTarget) => setLookupTarget(target);
  const closeLookup = () => setLookupTarget(null);

  const handleUserConfirm = (users: LookupUser[]) => {
    if (lookupTarget === 'cabManager') {
      onChange({ cabManager: users[0] ?? null });
    } else if (lookupTarget === 'boardMembers') {
      onChange({ boardMembers: users });
    }
    closeLookup();
  };

  const handleGroupConfirm = (groups: LookupGroup[]) => {
    if (lookupTarget === 'delegates') {
      onChange({ delegates: groups[0] ?? null });
    } else if (lookupTarget === 'boardGroups') {
      onChange({ boardGroups: groups });
    }
    closeLookup();
  };

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
        <div className="space-y-4">
          <div className="grid grid-cols-12 gap-3 items-start">
            <div className="col-span-12 sm:col-span-4 lg:col-span-3 pt-2 text-right sm:pr-2">
              <label htmlFor="meeting-name" className="text-sm font-medium text-gray-700">
                <span className="text-red-500 mr-0.5" aria-hidden>*</span>
                Name
              </label>
            </div>
            <div className="col-span-12 sm:col-span-8 lg:col-span-9">
              <input
                id="meeting-name"
                type="text"
                value={formData.name}
                maxLength={255}
                onChange={(e) => onChange({ name: e.target.value })}
                className={`w-full px-3 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 ${
                  errors.name ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
            </div>
          </div>

          <LookupField
            id="meeting-cab-manager"
            label="CAB Manager"
            required
            mode="single"
            displayValue={formData.cabManager ? formatUserDisplayName(formData.cabManager) : undefined}
            onOpenLookup={() => openLookup('cabManager')}
            onClear={() => onChange({ cabManager: null })}
            error={errors.cabManager}
          />

          <LookupField
            id="meeting-delegates"
            label="Delegates"
            mode="single"
            displayValue={formData.delegates ? formatGroupDisplayName(formData.delegates) : undefined}
            onOpenLookup={() => openLookup('delegates')}
            onClear={() => onChange({ delegates: null })}
            placeholder="Select group"
          />

          <LookupField
            id="meeting-board-members"
            label="Board Members"
            mode="multi"
            chipItems={formData.boardMembers.map((u) => ({
              id: u.id,
              label: formatUserDisplayName(u),
            }))}
            onOpenLookup={() => openLookup('boardMembers')}
            onRemoveChip={(id) =>
              onChange({ boardMembers: formData.boardMembers.filter((u) => u.id !== id) })
            }
            onClear={() => onChange({ boardMembers: [] })}
            placeholder="Select users"
          />

          <LookupField
            id="meeting-board-groups"
            label="Board Groups"
            mode="multi"
            chipItems={formData.boardGroups.map((g) => ({
              id: g.id,
              label: formatGroupDisplayName(g),
            }))}
            onOpenLookup={() => openLookup('boardGroups')}
            onRemoveChip={(id) =>
              onChange({ boardGroups: formData.boardGroups.filter((g) => g.id !== id) })
            }
            onClear={() => onChange({ boardGroups: [] })}
            placeholder="Select groups"
          />
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-12 gap-3 items-start">
            <div className="col-span-12 sm:col-span-4 lg:col-span-3 pt-2 text-right sm:pr-2">
              <label htmlFor="meeting-state" className="text-sm font-medium text-gray-700">
                <span className="text-red-500 mr-0.5" aria-hidden>*</span>
                State
              </label>
            </div>
            <div className="col-span-12 sm:col-span-8 lg:col-span-9">
              <select
                id="meeting-state"
                value={formData.status}
                onChange={(e) =>
                  onChange({ status: e.target.value as CabMeetingFormData['status'] })
                }
                className={`w-full px-3 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 ${
                  errors.status ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                {CAB_MEETING_STATE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {errors.status && <p className="mt-1 text-xs text-red-600">{errors.status}</p>}
            </div>
          </div>

          <div className="grid grid-cols-12 gap-3 items-start">
            <div className="col-span-12 sm:col-span-4 lg:col-span-3 pt-2 text-right sm:pr-2">
              <label htmlFor="meeting-start" className="text-sm font-medium text-gray-700">
                <span className="text-red-500 mr-0.5" aria-hidden>*</span>
                Meeting Start Time
              </label>
            </div>
            <div className="col-span-12 sm:col-span-8 lg:col-span-9">
              <DateTimePickerInput
                id="meeting-start"
                value={formData.meetingStart}
                onChange={(v) => onChange({ meetingStart: v })}
                showTimeSelect
                error={Boolean(errors.meetingStart || errors.dateTimeRange)}
              />
              {errors.meetingStart && (
                <p className="mt-1 text-xs text-red-600">{errors.meetingStart}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-12 gap-3 items-start">
            <div className="col-span-12 sm:col-span-4 lg:col-span-3 pt-2 text-right sm:pr-2">
              <label htmlFor="meeting-end" className="text-sm font-medium text-gray-700">
                <span className="text-red-500 mr-0.5" aria-hidden>*</span>
                Meeting End Time
              </label>
            </div>
            <div className="col-span-12 sm:col-span-8 lg:col-span-9">
              <DateTimePickerInput
                id="meeting-end"
                value={formData.meetingEnd}
                onChange={(v) => onChange({ meetingEnd: v })}
                showTimeSelect
                error={Boolean(errors.meetingEnd || errors.dateTimeRange)}
              />
              {(errors.meetingEnd || errors.dateTimeRange) && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.dateTimeRange || errors.meetingEnd}
                </p>
              )}
            </div>
          </div>

          {onNewMeetingInvite && (
            <div className="grid grid-cols-12 gap-3 items-start">
              <div className="col-span-12 sm:col-span-4 lg:col-span-3" />
              <div className="col-span-12 sm:col-span-8 lg:col-span-9">
                <button
                  type="button"
                  onClick={onNewMeetingInvite}
                  disabled={Boolean(newMeetingInvitePending)}
                  className="px-4 py-2 text-sm font-medium text-white bg-teal-700 rounded-md hover:bg-teal-800 disabled:opacity-50"
                >
                  {newMeetingInvitePending ? 'Sending invitation...' : 'New Meeting Invite'}
                </button>
                <p className="mt-1 text-xs text-gray-500">
                  Send an updated Outlook/Teams invitation with the latest meeting details to all
                  current participants.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 border border-gray-200 rounded-md overflow-hidden">
        <div className="border-b border-gray-200 bg-gray-50">
          <nav className="flex" aria-label="Meeting content tabs">
            <button
              type="button"
              onClick={() => onContentTabChange('conference')}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                contentTab === 'conference'
                  ? 'border-green-500 text-gray-900 bg-white'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Conference Information
            </button>
            <button
              type="button"
              onClick={() => onContentTabChange('notes')}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                contentTab === 'notes'
                  ? 'border-green-500 text-gray-900 bg-white'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Meeting Notes
            </button>
          </nav>
        </div>
        <div className="p-4">
          {contentTab === 'conference' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Conference details
              </label>
              <RichTextEditor
                value={formData.conferenceDetails}
                onChange={(value) => onChange({ conferenceDetails: value })}
                placeholder="Paste Microsoft Teams meeting invite or enter conference details..."
                minHeight="200px"
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meeting Notes
              </label>
              <div className="max-h-80 overflow-y-auto rounded-md border border-gray-200 bg-white px-3 py-3">
                {(formData.meetingNotes || '')
                  .split(/\r?\n/)
                  .map((l) => l.trim())
                  .filter(Boolean).length === 0 ? (
                  <p className="text-sm text-gray-500">
                    No meeting notes yet. Notes are added automatically in CAB WorkBench when Change
                    Requests receive approval decisions.
                  </p>
                ) : (
                  <div className="space-y-2 font-mono text-sm text-gray-800">
                    {(formData.meetingNotes || '')
                      .split(/\r?\n/)
                      .map((l) => l.trim())
                      .filter(Boolean)
                      .map((line, idx) => (
                        <div key={`${idx}-${line.slice(0, 32)}`} className="break-words">
                          {line}
                        </div>
                      ))}
                  </div>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Format: (Meeting Name) - (Change Number) - (Decision) - (Approver/Status) - (HH:mm:ss)
                - IST
              </p>
            </div>
          )}
        </div>
      </div>

      <UserLookupModal
        isOpen={lookupTarget === 'cabManager' || lookupTarget === 'boardMembers'}
        onClose={closeLookup}
        mode={lookupTarget === 'boardMembers' ? 'multi' : 'single'}
        title="Users"
        initialSelected={
          lookupTarget === 'cabManager'
            ? formData.cabManager
              ? [formData.cabManager]
              : []
            : formData.boardMembers
        }
        onConfirm={handleUserConfirm}
      />

      <GroupLookupModal
        isOpen={lookupTarget === 'delegates' || lookupTarget === 'boardGroups'}
        onClose={closeLookup}
        mode={lookupTarget === 'boardGroups' ? 'multi' : 'single'}
        title="Groups"
        groupTypes={[...CAB_BOARD_GROUP_TYPES]}
        initialSelected={
          lookupTarget === 'delegates'
            ? formData.delegates
              ? [formData.delegates]
              : []
            : formData.boardGroups
        }
        onConfirm={handleGroupConfirm}
      />
    </>
  );
};

export default CabMeetingForm;
